import { memo } from "react"

import type { ReportingQueryProps } from "../reporting-query"
import { AgentPerformance, AiResolutionRateChart } from "@/components/dashboard"
import { ImplementationChart } from "@/components/dashboard/implementation-chart"
import {
	formatDurationHours,
	formatDurationHoursNodes,
	ReportingBarChart,
	reportingCardClassName,
	ReportingKpiCard,
	ReportingMetricTrendCard,
	ReportingRankedList,
} from "@/components/reporting/charts"
import { api } from "@/lib/api"

export const OverviewTab = memo((props: ReportingQueryProps) => {
	const { startDate, endDate, ...filters } = props
	const query = api.workspace.getReportingKeyMetrics.useQuery(
		{ startDate, endDate, ...filters },
		{ cacheTime: Infinity, staleTime: 60_000 },
	)
	const data = query.data
	const loading = query.isLoading

	return (
		<div className="space-y-4 p-4 sm:p-6">
			<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
				<ReportingMetricTrendCard
					title="Median first response time"
					description="Median hours from conversation created to first human outbound reply."
					value={
						data?.medianFirstResponseHours != null
							? formatDurationHoursNodes(data.medianFirstResponseHours)
							: "—"
					}
					data={data?.medianFirstResponseSeries ?? []}
					isLoading={loading}
					tone="categorical-0"
					valueFormatter={formatDurationHours}
				/>
				<ReportingMetricTrendCard
					title="Median resolution time"
					description="Median create-to-Done time for conversations created in the selected range."
					value={
						data?.medianResolutionHours != null
							? formatDurationHoursNodes(data.medianResolutionHours)
							: "—"
					}
					data={data?.medianResolutionSeries ?? []}
					isLoading={loading}
					tone="categorical-1"
					valueFormatter={formatDurationHours}
				/>
				<ReportingMetricTrendCard
					title="Queue depth (in Todo)"
					description="Open conversations still in Todo or Snoozed at end of each day."
					value={data?.queueDepth ?? "—"}
					data={data?.queueDepthSeries ?? []}
					isLoading={loading}
					tone="categorical-2"
				/>
				<ImplementationChart
					startDate={startDate}
					endDate={endDate}
					{...filters}
				/>
			</div>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<ReportingBarChart
					title="New conversations per day"
					description="Conversations created each day in the selected range."
					data={(data?.newPerDay ?? []).map((p) => ({
						date: p.date,
						value: p.value,
					}))}
					series={[{ key: "value", label: "New", tone: "categorical-3" }]}
					average={data?.newPerDayAverage}
					isLoading={loading}
				/>
				<AiResolutionRateChart
					startDate={startDate}
					endDate={endDate}
					{...filters}
				/>
			</div>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
				<ReportingRankedList
					title="Companies"
					description="Conversation volume by company vs the previous equal period."
					items={data?.companies ?? []}
					isLoading={loading}
					tone="categorical-0"
				/>
				<ReportingRankedList
					title="Tags"
					description="Conversation volume by tag vs the previous equal period."
					items={data?.tags ?? []}
					isLoading={loading}
					tone="categorical-3"
				/>
				<ReportingRankedList
					title="Tiers"
					description="Conversation volume by company tier vs the previous equal period."
					items={data?.tiers ?? []}
					isLoading={loading}
					tone="categorical-2"
				/>
			</div>

			<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
				{[
					{
						label: "New",
						value: data?.kpi.newConversations,
						hint: "Created in selected range",
					},
					{
						label: "Closed",
						value: data?.kpi.closed,
						hint: "Created in range and now Done",
					},
					{
						label: "SLA miss %",
						value: data?.kpi.slaMissRate,
						hint: "Missed among threads created in range with an SLA",
					},
					{
						label: "AI resolution %",
						value: data?.kpi.aiResolutionRate,
						hint: "Resolved by AI session, or closed unassigned with AI reply and no human teammate reply",
					},
				].map((kpi) => (
					<ReportingKpiCard
						key={kpi.label}
						label={kpi.label}
						value={kpi.value}
						hint={kpi.hint}
						isLoading={loading}
					/>
				))}
			</div>

			<div className={`${reportingCardClassName} overflow-hidden`}>
				<div className="mb-2 px-5 pt-5">
					<h2 className="text-xs font-semibold tracking-wider text-tertiary">
						Team
					</h2>
				</div>
				<AgentPerformance
					startDate={startDate}
					endDate={endDate}
					{...filters}
				/>
			</div>
		</div>
	)
})
